import React from 'react';
import {get as _get} from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {Header, Segment} from 'semantic-ui-react';

import {gql, graphql, withApollo} from 'react-apollo';
import {showMessageBox} from '@utils/messageBox';
import HeaderRenderer from '@appComponents/HeaderRenderer';
import {FilterQueryWrapper, FilterUrlParamsWrapper} from '@appComponents/HOCFiltersQueryWrapper';
import {restoreFilterValues} from '@appComponents/HOCFiltersWrapper';
import {getSearchFilters} from '@utils/filters';
import mapModulesToProps from '@utils/mapModulesToProps';
import navigation from '@constants/navigation';
import {
    ENCODING_STATUS_ENCODING,
    ENCODING_STATUS_ERROR,
    ENCODING_STATUS_PREPARING,
    ENCODING_STATUS_SCHEDULED,
    ORIGIN_STREAM_TYPE_UDP,
    ORIGIN_STREAM_TYPE_SRT,
} from '@constants/variables';
import {convertToInt} from '@utils/helpers';
import {liveControlFilterQuery, liveControlIndexQuery} from '@graphql/liveControl';
import LiveControlTable from '@modules/liveControl/components/LiveControlTable/LiveControlTable';
import LiveControlFilters from '@modules/liveControl/components/LiveControlFilters';
import {failoverContentsMessageBox, udpateUdpOriginEntryPoint} from '@modules/liveControl/utils/helpers';
import {hideModal, showModalConfirmation} from '@utils/modal';

import LiveControlLegend from './LiveControlLegend';
import LiveControlMultipleFailoverConfirmation from './LiveControlMultipleFailoverConfirmation';

export class LiveControlIndex extends React.Component {
    static propTypes = {
        autoRefreshInterval: PropTypes.number,
        client: PropTypes.shape({
            mutate: PropTypes.func.isRequired,
        }),
        LiveControl: PropTypes.object,
        LiveControlFilter: PropTypes.shape({
            broadcastStatuses: PropTypes.array,
            distributionTypes: PropTypes.array,
            encodingDatacenters: PropTypes.array,
            encodingStatuses: PropTypes.array,
            eventTypes: PropTypes.array,
            products: PropTypes.array,
            propertyLicencesForDropdown: PropTypes.array,
            loading: PropTypes.bool,
            search: PropTypes.string,
            tvChannels: PropTypes.array,
            eventContentTypes: PropTypes.array,
            eventContentVariants: PropTypes.array,
        }),
        Menu: PropTypes.object,
        Modal: PropTypes.shape({
            setModalConfirmation: PropTypes.func.isRequired,
            setModal: PropTypes.func.isRequired,
        }),
        filters: PropTypes.object,
        Filters: PropTypes.object,
        loading: PropTypes.bool,
        loadingRefetch: PropTypes.bool.isRequired,
        liveControlAutoFailoverClosedTimestamp: PropTypes.string,
        timezone: PropTypes.string.isRequired,
        sessionFilters: PropTypes.object,
    };

    static defaultProps = {
        liveControlAutoFailoverClosedTimestamp: null,
        filters: {
            preBuffer: 2,
            postBuffer: 2,
        },
    };

    state = {
        liveControlList: [],
        pollingInterval: 20,
    };

    queryIntervalTimeoutHandler = null;

    componentDidMount() {
        this.props.Menu.storeMenu(navigation.liveControl.key);

        this.props.Filters.setFilter({
            name: 'LiveControlAutorefresh',
            data: {
                autorefresh_interval: this.props.autoRefreshInterval,
            },
        });
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        const liveControlList = nextProps.LiveControl.liveControlList;

        if (liveControlList && prevState.liveControlList !== liveControlList) {
            return {
                liveControlList: liveControlList,
            };
        }

        return null;
    }

    componentDidUpdate(prevProps) {
        if (this.props.LiveControl && this.props.LiveControl !== prevProps.LiveControl
            && !this.props.LiveControl.loading && this.props.LiveControl.liveControlList
            && this.props.LiveControl.liveControlList !== prevProps.LiveControl.liveControlList)
        {
            if (this.props.filters.lastClickSubmit && this.props.filters.lastClickSubmit !== this.state.formSubmitTimestamp) {
                this.setState(() => ({
                    formSubmitTimestamp: this.props.filters.lastClickSubmit,
                }));
            }
            this.setQueryTimeout(this.props.autoRefreshInterval);

            let autoFailoverContents = [],
                lastFailoverDatetime = '';

            this.props.LiveControl.liveControlList.forEach((listItem) => {
                if (moment.tz(listItem.origin_entry_point_auto_failover_datetime, this.props.timezone)
                    .isAfter(moment.tz(this.props.timezone).subtract(5, 'minutes'))) {
                    if ([ORIGIN_STREAM_TYPE_SRT, ORIGIN_STREAM_TYPE_UDP].includes(convertToInt(listItem.origin_stream_type_id))) {
                        autoFailoverContents.push(listItem);
                    }

                    if (lastFailoverDatetime < listItem.origin_entry_point_auto_failover_datetime) {
                        lastFailoverDatetime = listItem.origin_entry_point_auto_failover_datetime;
                    }
                }
            });

            if (!this.props.liveControlAutoFailoverClosedTimestamp
                || moment.tz(lastFailoverDatetime, this.props.timezone)
                    .isAfter(moment.tz(this.props.liveControlAutoFailoverClosedTimestamp, this.props.timezone))
            ) {
                failoverContentsMessageBox(autoFailoverContents);
            }
        }
    }

    setPollingInterval = (intervalValue) => {
        const interval = convertToInt(intervalValue);

        this.setState(() => ({
            pollingInterval: interval,
        }));

        clearTimeout(this.queryIntervalTimeoutHandler);

        this.props.Filters.setFilter({name: 'LiveControlAutorefresh', data: {autorefresh_interval: interval}});

        if (0 < interval && interval !== this.state.pollingInterval) {
            if (this.props.LiveControl?.refetch) {
                this.props.LiveControl.refetch();
            }
        }
    };

    setQueryTimeout = (interval) => {
        //Polling interval is not working in current apollo client so we use timeout instead
        clearTimeout(this.queryIntervalTimeoutHandler);

        if (0 < interval) {
            this.queryIntervalTimeoutHandler = setTimeout(() => {
                if (this.props.LiveControl?.refetch) {
                    this.props.LiveControl.refetch().finally(() => {
                        this.setQueryTimeout(interval);
                    });
                }
            }, interval * 1000);
        }
    };

    componentWillUnmount() {
        clearTimeout(this.queryIntervalTimeoutHandler);
    }

    handleMultipleUdpOriginEntryPointFailover = () => {
        showModalConfirmation({
            header: <Header content='UDP origin entry point failover'/>,
            text: <LiveControlMultipleFailoverConfirmation/>,
            onYes: () => {
                let eventContentUdpOriginEntryPointFailover = this.filterEventContentsForMultipleFailover();

                if (0 === eventContentUdpOriginEntryPointFailover.length) {
                    showMessageBox(
                        'liveControl',
                        'None of the event contents fulfilled the conditions for a UDP origin entry point failover!',
                        null,
                        'error'
                    );
                    hideModal();

                    return;
                }

                return udpateUdpOriginEntryPoint(eventContentUdpOriginEntryPointFailover, true);
            },
        });
    };

    filterEventContentsForMultipleFailover = () => (
        this.state.liveControlList.filter((eventContent) => (
            eventContent.event_content_has_input
            && ORIGIN_STREAM_TYPE_UDP === eventContent.origin_stream_type_id
            && null !== eventContent.udp_entry_point_id
            && null !== eventContent.failover_udp_entry_point_id
            && (
                null === eventContent.encoding_status_id
                || (
                    [
                        ENCODING_STATUS_SCHEDULED,
                        ENCODING_STATUS_PREPARING,
                        ENCODING_STATUS_ENCODING,
                        ENCODING_STATUS_ERROR,
                    ].includes(eventContent.encoding_status_id)
                    && eventContent.udp_entry_point_encoding_datacenter_id === eventContent.failover_udp_entry_point_encoding_datacenter_id
                )
            )
        )).map((eventContent) => ({
            event_content: convertToInt(eventContent.id),
            udp_origin_entry_point_failover: eventContent.failover_udp_entry_point_id,
            udp_origin_entry_point_failover_encoding_datacenter: convertToInt(
                eventContent.failover_udp_entry_point_encoding_datacenter_id
            ),
        }))
    );

    render() {
        const loading = (
            this.props.LiveControl.loading
            || this.props.loadingRefetch
        );

        return (<div>
            <HeaderRenderer
                pageTitle='Live Control'
                messagesBoxNames={['liveControl', 'liveControlAutoFailoverForUdp', 'liveControlAutoFailoverForSrt']}
                buttons={LiveControlLegend}
                buttonsProps={{
                    handleMultipleUdpOriginEntryPointFailover: this.handleMultipleUdpOriginEntryPointFailover,
                    loading: loading,
                    autoRefreshInterval: this.props.autoRefreshInterval,
                    refetchLiveControl: this.props.LiveControl.refetch,
                    setPollingInterval: this.setPollingInterval,
                }}
                filters={LiveControlFilters}
                filtersProps={
                    {
                        loading: this.props.LiveControlFilter.loading,
                        data: {
                            filters: this.props.filters,
                            restoreFilterValues: restoreFilterValues,
                            encodingStatuses: this.props.LiveControlFilter.encodingStatuses || [],
                            propertyLicencesForDropdown: this.props.LiveControlFilter.propertyLicencesForDropdown || [],
                            eventTypes: this.props.LiveControlFilter.eventTypes || [],
                            tvChannels: this.props.LiveControlFilter.tvChannels || [],
                            products: this.props.LiveControlFilter.products || [],
                            distributionTypes: this.props.LiveControlFilter.distributionTypes || [],
                            encodingDatacenters: this.props.LiveControlFilter.encodingDatacenters || [],
                            eventContentTypes: this.props.LiveControlFilter.eventContentTypes || [],
                            eventContentVariants: this.props.LiveControlFilter.eventContentVariants || [],
                            broadcastStatuses: this.props.LiveControlFilter.broadcastStatuses || [],
                        },
                    }
                }
            />
            <Segment basic className='--table'>
                <LiveControlTable
                    liveControlList={this.state.liveControlList}
                    loading={loading}
                />
            </Segment>
        </div>);
    }
}

const mapDispatchToProps = mapModulesToProps(['Filters', 'Menu', 'Modal']);

const mapStateToProps = (state) => {
    return {
        filters: getSearchFilters(state, 'LiveControl'),
        autoRefreshInterval: _get(state, 'app.filters.LiveControlAutorefresh.params.autorefresh_interval', 20),
        liveControlAutoFailoverClosedTimestamp: _get(state.messages.closedDatetimes, 'liveControlAutoFailover'),
        timezone: state.timezone.timezone,
    };
};

const LiveControlWithQuery = compose(
    graphql(gql(liveControlIndexQuery), {
        options: (props) => {
            let eventContentTypes = props.filters.eventContentTypes,
                eventContentVariants = props.filters.eventContentVariants,
                firstStageCategories = props.filters.firstStageCategories,
                secondStageCategories = props.filters.secondStageCategories;

            if (props.filters.eventContentVariants) {
                eventContentVariants = props.filters.eventContentVariants.map(id => convertToInt(id));
            }

            if (props.filters.eventContentTypes) {
                eventContentTypes = props.filters.eventContentTypes.map(id => convertToInt(id));
            }

            if (props.filters.firstStageCategories) {
                firstStageCategories = props.filters.firstStageCategories.map(id => convertToInt(id));
            }

            if (props.filters.secondStageCategories) {
                secondStageCategories = props.filters.secondStageCategories.map(id => convertToInt(id));
            }

            return {
                notifyOnNetworkStatusChange: true,
                fetchPolicy: 'network-only',
                variables: {
                    search: props.filters.search || null,
                    broadcastStatus: props.filters.broadcastStatus || null,
                    encodingStatus: props.filters.encodingStatus || null,
                    sport: props.filters.sports || null,
                    tvChannels: props.filters.tvChannels || null,
                    tournamentCategory: props.filters.tournamentCategories || null,
                    tournament: props.filters.tournaments || null,
                    court: props.filters.court || null,
                    propertyLicence: props.filters.propertyLicences || null,
                    eventType: props.filters.eventType || null,
                    contentCategoryLevel1: props.filters.contentCategoriesLevel1 || null,
                    contentCategoryLevel2: props.filters.contentCategoriesLevel2 || null,
                    contentCategoryLevel3: props.filters.contentCategoriesLevel3 || null,
                    product: props.filters.products || null,
                    preBuffer: _get(props.filters, 'preBuffer', 2),
                    postBuffer: _get(props.filters, 'postBuffer', 2),
                    distributionType: props.filters.distributionTypes || null,
                    encodingDatacenter: props.filters.encodingDatacenter || null,
                    startBroadcastAutomatically: (null !== props.filters.startBroadcastAutomatically
                    && props.filters.startBroadcastAutomatically !== undefined
                        ? !!props.filters.startBroadcastAutomatically : null),
                    eventContentType: eventContentTypes || null,
                    eventContentVariant: eventContentVariants || null,
                    firstStageCategory: firstStageCategories || null,
                    secondStageCategory: secondStageCategories || null,
                },
            };
        },
        name: 'LiveControl',
    }),
    graphql(gql(liveControlFilterQuery), {
        options: () => {
            return {
                fetchPolicy: 'cache-first',
                notifyOnNetworkStatusChange: true,
            };
        },
        name: 'LiveControlFilter',
    })
)(FilterQueryWrapper(LiveControlIndex, {
    excluded: ['autorefresh_interval'],
    queryForRefresh: 'LiveControl',
    filterUrls: ['liveControl'],
}));

export default withApollo(connect(mapStateToProps, mapDispatchToProps)(
    FilterUrlParamsWrapper(LiveControlWithQuery, LiveControlIndex.defaultProps.filters)));
